import type { MouseEvent, ReactNode } from "react";

import { cn } from "../../shared/ui/cn";

/**
 * One row of a master list. With an `href` it is a real link (middle click and
 * copy-link work) that navigates through `onSelect`; without, a plain button.
 * 44px minimum height keeps it a touch target on narrow screens.
 */
export function AdminListRow({
  href,
  selected,
  onSelect,
  children,
}: {
  href?: string;
  selected: boolean;
  onSelect: () => void;
  children: ReactNode;
}) {
  const className = cn(
    "flex min-h-11 w-full min-w-0 flex-col gap-1 border-l-[3px] px-3 py-2.5 text-left",
    "transition-[background-color,border-color] motion-fast hover:bg-hover-overlay",
    "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus",
    selected
      ? "border-accent bg-accent-subtle"
      : "border-transparent bg-transparent",
  );
  if (href === undefined) {
    return (
      <button
        type="button"
        aria-current={selected ? "true" : undefined}
        className={className}
        onClick={onSelect}
      >
        {children}
      </button>
    );
  }
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    // Let the browser handle new-tab gestures.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0)
      return;
    event.preventDefault();
    onSelect();
  };
  return (
    <a
      href={href}
      aria-current={selected ? "true" : undefined}
      className={className}
      onClick={onClick}
    >
      {children}
    </a>
  );
}

/** Sticky, scrollable master column container. */
export function MasterPane({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <section
      aria-label={label}
      className="flex min-w-0 flex-col gap-3 min-[900px]:sticky min-[900px]:top-2 min-[900px]:max-h-[calc(100dvh-1rem)] min-[900px]:overflow-y-auto"
    >
      {children}
    </section>
  );
}
